import * as THREE from 'three'
import { Texture } from 'three'
import {
  addBaseLoadResourceTasks,
  getBaseUniforms,
  getDefaultTextures,
  loadTexture,
} from '../../../trillion-common/src/gems/baseGem'
import { IGem } from '../../../trillion-common/src/gems/IGem'

function asherGem(): IGem {
  const name = 'asher'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/asherv3_NormalSpheremap_1.png'))
    //TODO don't forget to delete this example
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 2.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.1 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.5 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function baguetteGem(): IGem {
  const name = 'baguette'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/baguette_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec39.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)

    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 5.0 },
      _NormalOutZrot: { value: 1.0 },
      _NormalOutXrot: { value: 1.0 },
      _NormalOutYrot: { value: 1.0 },
      _NormalZrot: { value: 0.0 },
      _NormalXrot: { value: 1.0 },
      _NormalYrot: { value: 0.0 },
      _NormalOutReflect: { value: 1.0 },
      _InNegX: { value: -1.0 },
      _InNegY: { value: 1.0 },
      _InNegZ: { value: 1.0 },
      _yNormalScale: { value: 1.0 },
      _OutNegX: { value: -1.0 },
      _OutNegZ: { value: 1.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.6 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function radiantGem(): IGem {
  const name = 'radiant'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/radiant_NormalSpheremap-0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/diamond_00.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)

    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 2.4 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.6 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.7 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function diamondGem(): IGem {
  const name = 'diamond'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/diamond_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _RefractionIndex: { value: 2.4 },
      _NormalOutZrot: { value: 1.0 },
      _NormalOutXrot: { value: 3.0 },
      _NormalOutYrot: { value: 1.7 },
      _NormalZrot: { value: 0.0 },
      _NormalXrot: { value: 3.0 },
      _NormalYrot: { value: 0.0 },
      _NormalOutReflect: { value: 1.0 },
      _InNegX: { value: -1.0 },
      _InNegY: { value: 1.0 },
      _InNegZ: { value: 1.0 },
      _yNormalScale: { value: 1.0 },
      _OutNegX: { value: -1.0 },
      _OutNegZ: { value: 1.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3 },
      _OutSpecular: { value: 1.0 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function emeraldGem(): IGem {
  const name = 'emerald'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/emerald_NormalSpheremap_1.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 3.0 },
      _NormalOutZrot: { value: 1.0 },
      _NormalOutXrot: { value: 1.0 },
      _NormalOutYrot: { value: 1.0 },
      _NormalZrot: { value: 0.0 },
      _NormalXrot: { value: 1.0 },
      _NormalYrot: { value: -2.0 },
      _NormalOutReflect: { value: 1.0 },
      _InNegX: { value: -1.0 },
      _InNegY: { value: 1.0 },
      _InNegZ: { value: 1.0 },
      _yNormalScale: { value: 1.0 },
      _OutNegX: { value: -1.0 },
      _OutNegZ: { value: 1.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.4 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function hexagonGem(): IGem {
  const name = 'hexagon'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/hexagon_NormalSpheremap.png'))
    addBaseLoadResourceTasks(loadResorceTasks)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _OutNegY: { value: 1.17 },
      _Contrast: { value: 1.2 },
      _Brightness: { value: 0 },
      _InnerSpecular: { value: 0.0 },
      _OutSpecular: { value: 0.5 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function marquiseGem(): IGem {
  const name = 'marquise'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/marquise_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.3 },
      _Brightness: { value: -0.1 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.5 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function ovalGem(): IGem {
  const name = 'oval'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/oval_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _OutNegY: { value: -1 },
      _Contrast: { value: 2.5 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.8 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function pearGem(): IGem {
  const name = 'pear'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/pear_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 1.8 },
      _OutNegY: { value: 0.85 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3 },
      _OutSpecular: { value: 0.5 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function princessGem(): IGem {
  const name = 'princess'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/princess_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 2.4 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.5 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.5 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function trillionGem(): IGem {
  const name = 'trillion'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/TrillionTest/trillion_NormalSpheremap_0_0_0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _OutNegY: { value: 1.0 },
      _Contrast: { value: 2.9 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.8 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function cushionGem(): IGem {
  const name = 'cushion'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/cushion_NormalSpheremap_0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec39.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/spec37.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.7 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3 },
      _OutSpecular: { value: 0.6 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function heartGem(): IGem {
  const name = 'heart'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/heart_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/spec30.png'
    textures.inner_specular_d3 = 'Materials/spec37.png'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec19.png'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 3.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.6 },
      _Brightness: { value: -0.2 },
      _InnerSpecular: { value: 3.0 },
      _OutSpecular: { value: 0.7 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireDiamondGem(): IGem {
  const name = 'sapphire-diamond'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/diamond_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond35.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond35.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.2 },
      _Brightness: { value: 0.5 },
      _InnerSpecular: { value: 0.1 },
      _OutSpecular: { value: 0.3 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}
function blueSapphireDiamondGem(): IGem {
  const name = 'blue-sapphire-diamond'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/diamond_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/HDRStudio.hdr'
    textures.inner_specular_d3 = 'Materials/diamond63.hdr'
    textures.inner_specular_d2 = 'Materials/diamond63.hdr'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/DiamondEnvironmentMap2.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: -1.0 },
      _RefractionIndex: { value: 1.8 },
      _Contrast: { value: 1.6 },
      _Brightness: { value: 0.26 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.15 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireAscherGem(): IGem {
  const name = 'sapphire-ascher'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/asherv3_NormalSpheremap_1.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/HDRStudio8.hdr'
    textures.inner_specular_d3 = 'Materials/diamond18.hdr'
    textures.inner_specular_d2 = 'Materials/diamond10.hdr'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec10.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 2.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.2 },
      _Brightness: { value: 0.4 },
      _InnerSpecular: { value: 10.0 },
      _OutSpecular: { value: 0.4 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireAscherGemHermes(): IGem {
  const name = 'sapphire-ascher-hermes'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/asherv3_NormalSpheremap_1.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/HDRStudio8.hdr'
    textures.inner_specular_d3 = 'Materials/diamond18.hdr'
    textures.inner_specular_d2 = 'Materials/diamond10.hdr'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/spec10.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() },
      _RefractionIndex: { value: 2.0 },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.2 },
      _Brightness: { value: 0.4 },
      _InnerSpecular: { value: 10.0 },
      _OutSpecular: { value: 0.4 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireBaguetteGem(): IGem {
  const name = 'sapphire-baguette'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/baguette_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond44.hdr'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 1.0 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.2 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireRadiantGem(): IGem {
  const name = 'sapphire-radiant'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/radiant_NormalSpheremap-0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 3.8 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireEmeraldGem(): IGem {
  const name = 'sapphire-emerald'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/emerald_NormalSpheremap_1.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.2 },
      _InnerSpecular: { value: 0.2 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireMarquiseGem(): IGem {
  const name = 'sapphire-marquise'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/marquise_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 1.4 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.0 },
      _InnerSpecular: { value: 0.2 },
      _OutSpecular: { value: 0.3 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireOvalGem(): IGem {
  const name = 'sapphire-oval'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/oval_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond44.hdr'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 2.3 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.3 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphirePearGem(): IGem {
  const name = 'sapphire-pear'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/pear_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 0.85 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphirePrincessGem(): IGem {
  const name = 'sapphire-princess'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/princess_v1_NormalSpheremap.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 1.0 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }
  return { name, getUniforms }
}
function sapphireTrillionGem(): IGem {
  const name = 'sapphire-trillion'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/TrillionTest/trillion_NormalSpheremap_0_0_0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond15.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: 1.0 },
      _Contrast: { value: 2.0 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.3 },
      _OutSpecular: { value: 0.3 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

function sapphireCushionGem(): IGem {
  const name = 'sapphire-cushion'

  async function getUniforms(
    jewelry: THREE.Mesh,
    loadResorceTasks: Promise<THREE.Texture | string>[],
  ) {
    loadResorceTasks.push(loadTexture('Materials/cushion_NormalSpheremap_0.png'))
    const textures = getDefaultTextures()
    textures.skybox_texture = 'Materials/2blights_0.jpg'
    textures.inner_specular_d3 = 'Materials/diamond15.hdr'
    textures.inner_specular_d2 = 'Materials/diamond_00.png'
    textures.inner_specular_d1 = 'Materials/diamond_00.png'
    textures.inner_texture = 'Materials/diamond57.hdr'
    addBaseLoadResourceTasks(loadResorceTasks, textures)
    const resources = await Promise.all(loadResorceTasks)
    const uniforms = {
      ...getBaseUniforms(resources, jewelry),
      _NormalMap: { value: resources.pop() as Texture },
      _OutNegY: { value: -1.0 },
      _Contrast: { value: 2.3 },
      _Brightness: { value: 0.1 },
      _InnerSpecular: { value: 0.2 },
      _OutSpecular: { value: 0.2 },
    }
    return { resources, uniforms }
  }

  return { name, getUniforms }
}

export const gems = [
  asherGem(),
  baguetteGem(),
  diamondGem(),
  emeraldGem(),
  hexagonGem(),
  marquiseGem(),
  ovalGem(),
  pearGem(),
  princessGem(),
  trillionGem(),
  cushionGem(),
  radiantGem(),
  heartGem(),
  sapphireDiamondGem(),
  blueSapphireDiamondGem(),
  sapphireAscherGem(),
  sapphireAscherGemHermes(),
  sapphireBaguetteGem(),
  sapphireRadiantGem(),
  sapphireEmeraldGem(),
  sapphireMarquiseGem(),
  sapphireOvalGem(),
  sapphirePearGem(),
  sapphirePrincessGem(),
  sapphireTrillionGem(),
  sapphireCushionGem(),
]
